import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { dayjs } from '@koob/dates';
import {
  TripDayInsertBetween,
  useDays,
  useExperienceFrames,
  useExperienceRemovalUndo,
  useTripFilters,
  useTripStore,
} from '@koob/trip-designer';
import ComposeMapBlock from './ComposeMapBlock';
import ComposeProductAccessAlert from './ComposeProductAccessAlert';
import ProgramRowHeader from './ProgramRowHeader';
import { buildComposeBlocks } from './composeMapTrip';

const ExperiencesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing(1.5)};
  grid-template-columns: 100%;
  grid-template-rows: auto;
`;

const getBlockKey = ({ frame, removal, dayIndices }) => {
  const prefix = frame ? 'frame' : removal ? 'removed' : 'days';

  return `${prefix}-${dayIndices[0]}`;
};

// Products of this itinerary the DMC never distributed to us, counted once
// each however many days they span.
const countInaccessible = products =>
  Object.values(products ?? {}).filter(
    product => product?.isAccessible === false,
  ).length;

// The scrolling list of the trip: arrival, one block per frame or loose day,
// the "+" that appends a day, and departure.
export default function ComposeMapTimeline({
  registry,
  addExperience,
  addHotel,
}) {
  const { t } = useTranslation('tripDesigner');
  const tripFilters = useTripFilters();
  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const hotelsMap = useTripStore(state => state.hotels);
  const updateDays = useTripStore(state => state.updateDays);

  // KD-3581 — the timeline is rendered frame by frame. The grouping is a view
  // over `days`: the array itself is untouched, so day insertion, map focus and
  // the save payload keep indexing exactly as they did.
  const experienceFrames = useExperienceFrames();

  // AC09 — putting the trip back the way the day photographed it. On to the
  // additional services live on the days, so restoring the days restores every
  // selection with its adult/child split.
  const restoreRemovedExperience = useCallback(
    snapshot => {
      if (!snapshot) {
        return;
      }

      updateDays(snapshot.days);
    },
    [updateDays],
  );

  const { pendingRemoval, captureRemoval, undoRemoval } =
    useExperienceRemovalUndo({ restore: restoreRemovedExperience });

  const composeBlocks = useMemo(
    () =>
      buildComposeBlocks({
        experienceFrames,
        dayCount: days?.length ?? 0,
        pendingRemoval,
      }),
    [experienceFrames, days?.length, pendingRemoval],
  );

  // The arrival row names where the travellers land. The trip's own start city
  // is optional — it is a wizard field nothing forces — and the row was left
  // with a bare pin whenever it was missing. The itinerary always knows better
  // than nothing: its first frame opens somewhere.
  const arrivalLocation =
    tripFilters?.startLocation?.title ||
    experienceFrames?.[0]?.startLocation ||
    null;

  // And where they leave from: the last day of the trip, and the city the
  // itinerary ends in when the trip carries no end city of its own.
  const departureLocation =
    tripFilters?.endLocation?.title ||
    experienceFrames?.[experienceFrames.length - 1]?.endLocation ||
    null;

  return (
    <ExperiencesGrid>
      <ProgramRowHeader
        title={t('compose.arrival')}
        date={tripFilters.departureDate}
        location={arrivalLocation}
      />

      {!days && (
        <div className="bg-gray-100 px-6 py-4 rounded-lg">
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <svg className="h-10 w-10" viewBox="0 0 512 512">
              <path d="M256 32a224 224 0 1 1 0 448 224 224 0 1 1 0-448zm0 480A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM208 352c-8.8 0-16 7.2-16 16s7.2 16 16 16h96c8.8 0 16-7.2 16-16s-7.2-16-16-16H272V240c0-8.8-7.2-16-16-16H216c-8.8 0-16 7.2-16 16s7.2 16 16 16h24v96H208zm48-168a24 24 0 1 0 0-48 24 24 0 1 0 0 48z" />
            </svg>

            <div>{t('compose.noDays')}</div>
          </div>
        </div>
      )}

      <ComposeProductAccessAlert
        variant="global"
        count={countInaccessible(experiencesMap) + countInaccessible(hotelsMap)}
      />

      <div>
        {composeBlocks.map(block => (
          <ComposeMapBlock
            key={getBlockKey(block)}
            block={block}
            registry={registry}
            onUndo={undoRemoval}
            onExperienceRemoved={captureRemoval}
            addExperience={addExperience}
            addHotel={addHotel}
          />
        ))}
      </div>

      {/* Appending a day belongs to the trip, not to the experience
      that happens to end it: rendered from the last day card, this
      "+" landed inside the frame and above the global services
      band, which closes the frame (KD-3581 AC03). It sits at
      timeline level instead, under the whole block. The component
      still self-filters on the trip's last day, so the index it is
      given is the only thing that decides whether it shows. */}
      <TripDayInsertBetween
        dayIndex={(days?.length ?? 1) - 1}
        position="after"
        translation={t}
      />

      {/* The itinerary closes the way it opens (§9): same row,
          filled bullet, on the last day of the trip. */}
      <ProgramRowHeader
        variant="departure"
        title={t('compose.departure')}
        date={dayjs(tripFilters.departureDate).add(
          Math.max((days?.length ?? 1) - 1, 0),
          'days',
        )}
        location={departureLocation}
      />
    </ExperiencesGrid>
  );
}
